import { onCLS, onINP, onLCP, onFCP, onTTFB, Metric } from 'web-vitals';

interface AnalyticsEvent {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
}

/**
 * Send Web Vitals to analytics
 * Supports Google Tag Manager (dataLayer) and custom endpoints
 */
const sendToAnalytics = (metric: Metric) => {
  const { name, value, rating, delta, id, navigationType } = metric;

  const event: AnalyticsEvent = {
    name,
    value: Math.round(name === 'CLS' ? value * 1000 : value),
    rating,
    delta: Math.round(delta),
    id,
    navigationType,
  };

  // Send to Google Tag Manager dataLayer if available
  if (typeof window !== 'undefined' && 'dataLayer' in window) {
    const dataLayer = (window as any).dataLayer;
    dataLayer.push({
      event: 'web_vitals',
      metric_name: name,
      metric_value: event.value,
      metric_id: id,
      metric_rating: rating,
      metric_delta: event.delta,
      navigation_type: navigationType,
    });
  }

  // Fallback to gtag if GTM not available
  if (typeof window !== 'undefined' && 'gtag' in window && !('dataLayer' in window)) {
    const gtag = (window as any).gtag;
    gtag('event', name, {
      value: event.value,
      metric_id: id,
      metric_value: event.value,
      metric_delta: event.delta,
      metric_rating: rating,
      event_category: 'Web Vitals',
      event_label: id,
      non_interaction: true,
    });
  }

  // Send to custom analytics endpoint (optional)
  if (import.meta.env.VITE_ANALYTICS_ENDPOINT) {
    navigator.sendBeacon?.(
      import.meta.env.VITE_ANALYTICS_ENDPOINT,
      JSON.stringify(event)
    );
  }

  // Log in development
  if (import.meta.env.DEV) {
    const color = rating === 'good' ? 'green' : rating === 'needs-improvement' ? 'orange' : 'red';
    console.log(`%c${name}: ${event.value}ms (${rating})`, `color: ${color}; font-weight: bold;`);
  }
};

/**
 * Initialize Web Vitals tracking
 * Call this once on app mount
 */
export const initWebVitals = () => {
  if (typeof window === 'undefined') return;

  try {
    // Core Web Vitals
    onCLS(sendToAnalytics);  // Cumulative Layout Shift
    onINP(sendToAnalytics);  // Interaction to Next Paint
    onLCP(sendToAnalytics);  // Largest Contentful Paint

    // Additional metrics
    onFCP(sendToAnalytics);  // First Contentful Paint
    onTTFB(sendToAnalytics); // Time to First Byte
  } catch (error) {
    console.error('Failed to initialize Web Vitals:', error);
  }
};

/**
 * Get thresholds for each metric
 */
export const getThresholds = (metric: string) => {
  const thresholds = {
    CLS: { good: 0.1, poor: 0.25 },
    INP: { good: 200, poor: 500 },
    LCP: { good: 2500, poor: 4000 },
    FCP: { good: 1800, poor: 3000 },
    TTFB: { good: 800, poor: 1800 },
  };

  return thresholds[metric as keyof typeof thresholds] || { good: 0, poor: 0 };
};

/**
 * Calculate rating based on value
 */
export const getRating = (metric: string, value: number): 'good' | 'needs-improvement' | 'poor' => {
  const { good, poor } = getThresholds(metric);
  if (value <= good) return 'good';
  if (value <= poor) return 'needs-improvement';
  return 'poor';
};

/**
 * Track custom performance metrics
 */
export const trackCustomMetric = (name: string, value: number, metadata?: Record<string, any>) => {
  if (typeof window !== 'undefined' && 'dataLayer' in window) {
    const dataLayer = (window as any).dataLayer;
    dataLayer.push({
      event: 'custom_metric',
      metric_name: name,
      metric_value: Math.round(value),
      ...metadata,
    });
  } else if (typeof window !== 'undefined' && 'gtag' in window) {
    const gtag = (window as any).gtag;
    gtag('event', 'custom_metric', {
      metric_name: name,
      metric_value: Math.round(value),
      event_category: 'Performance',
      ...metadata,
    });
  }

  if (import.meta.env.DEV) {
    console.log(`Custom metric: ${name} = ${Math.round(value)}ms`, metadata);
  }
};

/**
 * Track page load performance
 */
export const trackPageLoad = () => {
  if (typeof window === 'undefined' || !('performance' in window)) return;

  try {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    if (navigation) {
      const metrics = {
        dns: navigation.domainLookupEnd - navigation.domainLookupStart,
        tcp: navigation.connectEnd - navigation.connectStart,
        ttfb: navigation.responseStart - navigation.requestStart,
        download: navigation.responseEnd - navigation.responseStart,
        domInteractive: navigation.domInteractive - navigation.fetchStart,
        domComplete: navigation.domComplete - navigation.fetchStart,
        loadComplete: navigation.loadEventEnd - navigation.fetchStart,
      };

      Object.entries(metrics).forEach(([key, value]) => {
        if (value > 0) {
          trackCustomMetric(`page_load_${key}`, value);
        }
      });
    }
  } catch (error) {
    console.error('Failed to track page load:', error);
  }
};

/**
 * Track resource loading
 */
export const trackResourceLoading = () => {
  if (typeof window === 'undefined' || !('performance' in window)) return;

  try {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    
    const resourcesByType = resources.reduce((acc, resource) => {
      const type = resource.initiatorType;
      if (!acc[type]) acc[type] = { count: 0, totalDuration: 0, totalSize: 0 };
      
      acc[type].count++;
      acc[type].totalDuration += resource.duration;
      acc[type].totalSize += resource.transferSize || 0;
      
      return acc;
    }, {} as Record<string, { count: number; totalDuration: number; totalSize: number }>);

    if (import.meta.env.DEV) {
      console.table(resourcesByType);
    }

    // Track slow resources
    const slowResources = resources.filter(r => r.duration > 1000);
    if (slowResources.length > 0 && typeof window !== 'undefined') {
      if ('dataLayer' in window) {
        const dataLayer = (window as any).dataLayer;
        slowResources.forEach(resource => {
          dataLayer.push({
            event: 'slow_resource',
            resource_url: resource.name,
            resource_duration: Math.round(resource.duration),
            resource_type: resource.initiatorType,
          });
        });
      } else if ('gtag' in window) {
        const gtag = (window as any).gtag;
        slowResources.forEach(resource => {
          gtag('event', 'slow_resource', {
            resource_url: resource.name,
            resource_duration: Math.round(resource.duration),
            resource_type: resource.initiatorType,
            event_category: 'Performance',
          });
        });
      }
    }
  } catch (error) {
    console.error('Failed to track resource loading:', error);
  }
};
